import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './ProdutoList.css';
import api from '../services/api';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';

const PAGE_SIZE = 10;

const STATUS_OPTIONS = ['Pendente', 'Em Processamento', 'Enviado', 'Concluído', 'Cancelado'];

const STATUS_BADGE = {
  'Pendente':          { cls: 'badge-warn',  label: 'Pendente' },
  'Em Processamento':  { cls: 'badge-warn',  label: 'Em Processamento' },
  'Enviado':           { cls: 'badge-ok',    label: 'Enviado' },
  'Concluído':         { cls: 'badge-ok',    label: 'Concluído' },
  'Cancelado':         { cls: 'badge-error', label: 'Cancelado' },
};

function PedidoList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteFiltroId = searchParams.get('cliente');

  const [pedidos, setPedidos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState('');
  const [valorTotal, setValorTotal] = useState('');
  const [status, setStatus] = useState('Pendente');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState('');
  const [showForm, setShowForm] = useState(false);
  const [page, setPage]         = useState(1);
  const [sortKey, setSortKey]   = useState(null);
  const [sortDir, setSortDir]   = useState('asc');

  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchPedidos = async () => {
      try {
        const res = await api.get('/pedidos/', { headers });
        setPedidos(res.data);
      } catch {
        toast.error('Erro ao carregar pedidos.');
      } finally {
        setLoading(false);
      }
    };
    const fetchClientes = async () => {
      try {
        const res = await api.get('/clientes/', { headers });
        setClientes(res.data);
      } catch {
        toast.error('Erro ao carregar clientes.');
      }
    };
    fetchPedidos();
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post(
        '/pedidos/',
        { cliente, valor_total: parseFloat(valorTotal), status },
        { headers: { ...headers, 'Content-Type': 'application/json' } }
      );
      setPedidos([res.data, ...pedidos]);
      setCliente('');
      setValorTotal('');
      setStatus('Pendente');
      setShowForm(false);
      toast.success('Pedido criado com sucesso!');
    } catch {
      toast.error('Erro ao criar pedido.');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id, novoStatus) => {
    try {
      await api.patch(
        `/pedidos/${id}/`,
        { status: novoStatus },
        { headers: { ...headers, 'Content-Type': 'application/json' } }
      );
      setPedidos(pedidos.map((p) => (p.id === id ? { ...p, status: novoStatus } : p)));
      toast.success('Status atualizado!');
    } catch {
      toast.error('Erro ao atualizar status.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este pedido?')) return;
    try {
      await api.delete(`/pedidos/${id}/`, { headers });
      setPedidos(pedidos.filter((p) => p.id !== id));
      toast.success('Pedido excluído.');
    } catch {
      toast.error('Erro ao excluir pedido.');
    }
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  const formatDate = (str) => {
    if (!str) return '—';
    return new Date(str).toLocaleDateString('pt-BR');
  };

  const filtered = pedidos
    .filter((p) => !clienteFiltroId || String(p.cliente) === clienteFiltroId)
    .filter((p) => (p.cliente_nome || '').toLowerCase().includes(search.toLowerCase()));

  const nomeClienteFiltro = clientes.find((c) => String(c.id) === clienteFiltroId)?.nome;

  const limparFiltroCliente = () => {
    searchParams.delete('cliente');
    setSearchParams(searchParams);
  };
  const sorted = sortKey
    ? [...filtered].sort((a, b) => {
        const va = a[sortKey] ?? ''; const vb = b[sortKey] ?? '';
        const cmp = typeof va === 'string' ? va.localeCompare(vb, 'pt-BR') : Number(va) - Number(vb);
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  return (
    <div className="produto-page">

      {/* Header */}
      <div className="produto-page-header">
        <div className="produto-page-title-row">
          <h2 className="produto-page-title">Pedidos</h2>
          {clienteFiltroId && (
            <span className="badge badge-ok" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Filtrado por: {nomeClienteFiltro || `cliente #${clienteFiltroId}`}
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 14, cursor: 'pointer' }}
                onClick={limparFiltroCliente}
              >
                close
              </span>
            </span>
          )}
        </div>
        <div className="produto-header-actions">
          <div className="produto-search-wrap">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              placeholder="Buscar por cliente..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <button
            className="prd-btn-save"
            onClick={() => setShowForm((v) => !v)}
            style={{ whiteSpace: 'nowrap' }}
          >
            <span className="material-symbols-outlined">
              {showForm ? 'close' : 'add'}
            </span>
            {showForm ? 'Cancelar' : 'Novo Pedido'}
          </button>
        </div>
      </div>

      {/* New order form */}
      {showForm && <div className="prd-card prd-form-card">
        <div className="prd-card-accent" />
        <div className="prd-form-body">
          <header className="prd-form-header">
            <h3 className="prd-form-title">Novo Pedido</h3>
            <p className="prd-form-sub">Registre um novo pedido informando o cliente e valor.</p>
          </header>

          <form onSubmit={handleSubmit}>
            <div className="prd-form-left">
              <div className="prd-form-row3">
                <div className="prd-field">
                  <label className="prd-label">Cliente</label>
                  <select
                    className="prd-input"
                    value={cliente}
                    onChange={(e) => setCliente(e.target.value)}
                    required
                  >
                    <option value="">Selecione...</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                  {clientes.length === 0 && (
                    <span className="prd-label" style={{ color: '#ba1a1a', fontWeight: 400 }}>
                      Nenhum cliente cadastrado — <a href="/clientes/novo">cadastre um primeiro</a>.
                    </span>
                  )}
                </div>

                <div className="prd-field">
                  <label className="prd-label">Valor Total (R$)</label>
                  <div className="prd-input-prefix-wrap">
                    <span className="prd-input-prefix">R$</span>
                    <input
                      className="prd-input prd-input--prefixed"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0,00"
                      value={valorTotal}
                      onChange={(e) => setValorTotal(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="prd-field">
                  <label className="prd-label">Status</label>
                  <select
                    className="prd-input"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="prd-form-actions" style={{ gridColumn: 'unset', marginTop: '1.5rem' }}>
              <button type="submit" className="prd-btn-save" disabled={saving}>
                <span className="material-symbols-outlined">shopping_cart</span>
                {saving ? 'Criando...' : 'Criar Pedido'}
              </button>
            </div>
          </form>
        </div>
      </div>}

      {/* Orders table */}
      <div className="prd-card prd-table-card">
        <div className="prd-card-accent" />
        <div className="prd-table-toolbar">
          <span className="prd-table-count">
            {filtered.length} pedido{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="prd-table-wrap">
          <table className="prd-table">
            <thead>
              <tr>
                <th className={`th-sort${sortKey === 'cliente_nome' ? ' sort-active' : ''}`} onClick={() => handleSort('cliente_nome')}>
                  Cliente <span className="material-symbols-outlined th-sort-icon">{sortKey === 'cliente_nome' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}</span>
                </th>
                <th className={`th-sort${sortKey === 'data_pedido' ? ' sort-active' : ''}`} onClick={() => handleSort('data_pedido')}>
                  Data <span className="material-symbols-outlined th-sort-icon">{sortKey === 'data_pedido' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}</span>
                </th>
                <th className={`right th-sort${sortKey === 'valor_total' ? ' sort-active' : ''}`} onClick={() => handleSort('valor_total')}>
                  Valor Total <span className="material-symbols-outlined th-sort-icon">{sortKey === 'valor_total' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}</span>
                </th>
                <th className={`center th-sort${sortKey === 'status' ? ' sort-active' : ''}`} onClick={() => handleSort('status')}>
                  Status <span className="material-symbols-outlined th-sort-icon">{sortKey === 'status' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}</span>
                </th>
                <th className="right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j}><div className="skeleton" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="prd-empty">
                    {clienteFiltroId ? 'Nenhum pedido para este cliente.' : 'Nenhum pedido encontrado.'}
                  </td>
                </tr>
              ) : (
                paginated.map((pedido) => {
                  const badge = STATUS_BADGE[pedido.status] || { cls: 'badge-warn', label: pedido.status };
                  return (
                    <tr key={pedido.id}>
                      <td className="td-name">{pedido.cliente_nome}</td>
                      <td>{formatDate(pedido.data_pedido)}</td>
                      <td className="right td-price">{formatCurrency(pedido.valor_total)}</td>
                      <td className="center">
                        <select
                          className="prd-input"
                          style={{ padding: '4px 8px', fontSize: '12px', width: 'auto', minWidth: '140px' }}
                          value={pedido.status}
                          onChange={(e) => handleStatusChange(pedido.id, e.target.value)}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="right">
                        <div className="td-actions">
                          <button
                            className="prd-icon-btn prd-icon-btn--danger"
                            onClick={() => handleDelete(pedido.id)}
                            title="Excluir"
                          >
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={(p) => { setPage(p); window.scrollTo(0, 0); }} />
      </div>
    </div>
  );
}

export default PedidoList;
